import axios from 'axios'
import { normalize, schema } from 'normalizr'
import { original } from 'immer'
import uuid from 'uuid/v1'
import CreateReducer from '../utils/createReducer'
import CreateActions from '../utils/createActions'
import { mergeEntities, mergeAll } from '../utils/mergeNormalized'
import networkWrapper from '../utils/networkWrapper'
import api from '../api'

import selectors from '../selectors'
import snackbarActions from './snackbar'
import routerActions from './router'

import QuoteModel from 'shared/src/quote/model'
import quoteUtils from 'shared/src/quote/utils'

import PDFQuote from 'pdf/quote'

const quote = new schema.Entity('quote')

const prefix = 'quote'

const initialState = {

  changes: 0,

  quotes: normalize([], [quote]),

  // a map of item id -> item data
  items: {},

  // a map of item id -> children ids
  children: {},

  // the inputs we have given the quote
  inputs: {},

  // quote inputs we are currently editing
  edits: {},

  // if items are expanded in the quote view
  expanded: {},

  // the delete item the modal is open for
  deleteItem: null,

  // what is the parent id of where we are adding tasks
  taskWindowParent: null,

  // if we are editing the name of a row
  editName: null,

  // are we in sort mode
  sortMode: false,

  // are we looking the quote as the client
  // here we make everything read only
  // and merge the markups
  clientView: false,
}


const getItemChildrenIds = (children, itemId) => {
  const childIds = children[itemId] || []
  const descendentIds = childIds.reduce((all, childId) => {
    return all.concat(getItemChildrenIds(children, childId))
  }, [])
  return childIds.concat(descendentIds)
}

const reducers = {

  resetQuote: (state, action) => {
    state.items = {}
    state.children = {}
    state.inputs = {}
    state.edits = {}
    state.expanded = {}
    state.changes = 0
  },

  setChanges: (state, action) => {
    state.changes = action.payload
  },

  addChange: (state, action) => {
    state.changes += 1
  },

  resetChanges: (state, action) => {
    state.changes = 0
  },

  setQuotes: (state, action) => {
    state.quotes = normalize(action.payload, [quote])
  },

  setExpanded: (state, action) => {
    const {
      id,
      value,
    } = action.payload
    state.expanded[id] = value
  },

  setAllExpanded: (state, action) => {
    state.expanded = action.payload
  },

  setDeleteItem: (state, action) => {
    state.deleteItem = action.payload
  },

  setTaskWindowParent: (state, action) => {
    state.taskWindowParent = action.payload
  },

  setEditName: (state, action) => {
    state.editName = action.payload
  },

  resetEditName: (state, action) => {
    state.editName = null
  },

  commitEditName: (state, action) => {
    const {
      id,
      linkIndex,
      value,
    } = state.editName

    if(typeof(linkIndex) == 'number') {
      const item = state.items[id]
      item.resource.links[linkIndex].resource.name = value
    }
    else {
      const item = state.items[id]
      item.resource.name = value
    }

    
    state.editName = null
    state.changes += 1
  },

  setSortMode: (state, action) => {
    state.sortMode = action.payload
    if(action.payload) {
      const model = QuoteModel({
        items: state.items,
        children: state.children,
        expanded: state.expanded,
      })
      model.expandTopFolders()
    }
  },

  setClientView: (state, action) => {
    state.clientView = action.payload
  },

  setExpandedChildren: (state, action) => {
    const {
      id,
      value,
      type,
    } = action.payload

    if(type == 'rootfolders') {
      const model = QuoteModel({
        items: state.items,
        children: state.children,
        expanded: state.expanded,
      })
      model.expandTopFolders()
    }
    else {
      const expandIds = [id].concat(getItemChildrenIds(state.children, id))
      expandIds.forEach(id => {
        state.expanded[id] = value
      })
    }
  },
  setQuote: (state, action) => {
    const data = Object.assign({}, action.payload)
    const meta = Object.assign({}, data.meta)
    const items = meta.items
    const children = meta.children
    const inputs = meta.inputs
    delete(meta.items)
    delete(meta.children)
    delete(meta.inputs)
    data.meta = meta
    mergeEntities(state.quotes, normalize([data], [quote]))
    state.items = items || {}
    state.children = children || {}
    state.inputs = inputs || {}
    state.edits = {}
  },
  addItemData: (state, action) => {
    const {
      parent,
      item,
    } = action.payload

    const model = QuoteModel({
      items: state.items,
      children: state.children,
      expanded: state.expanded,
    })

    model.addItem({
      parent,
      item,
    })
    
  },
  updateItemData: (state, action) => {
    const {
      id,
      data,
    } = action.payload
    state.items[id] = data
  },
  onSetEdit: (state, action) => {
    const {
      id,
      linkIndex,
      field,
      value,
    } = action.payload
    if(typeof(linkIndex) == 'number') {
      const allLinkEdits = state.items[id].linkEdits || []
      const linkEdits = allLinkEdits[linkIndex] || {}
      linkEdits[field] = value
      allLinkEdits[linkIndex] = linkEdits
      state.items[id].linkEdits = allLinkEdits      
    }
    else {
      const edits = state.items[id].edits || {}
      edits[field] = value
      state.items[id].edits = edits
    }
  },
  onSetInput: (state, action) => {
    const {
      id,
      linkIndex,
      field,
      value,
    } = action.payload
    if(typeof(linkIndex) == 'number') {
      const allLinkInputs = state.items[id].linkInputs || []
      const linkInputs = allLinkInputs[linkIndex] || {}
      linkInputs[field] = value
      allLinkInputs[linkIndex] = linkInputs
      state.items[id].linkInputs = allLinkInputs      
    }
    else {
      const inputs = state.items[id].inputs || {}
      inputs[field] = value
      state.items[id].inputs = inputs
    }
  },
  onSetQuoteInput: (state, action) => {
    const {
      field,
      value,
    } = action.payload
    state.inputs[field] = value
  },
  onSetQuoteEdit: (state, action) => {
    const {
      field,
      value,
    } = action.payload
    state.edits[field] = value
  },
  deleteItemData: (state, action) => {
    const {
      id,
    } = action.payload
    const deleteItem = state.items[id]
    const parentChildren = state.children[deleteItem.parent]
    state.children[deleteItem.parent] = parentChildren.filter(childId => childId != id)
    const allIds = [id].concat(getItemChildrenIds(state.children, id))
    allIds.forEach(id => {
      delete(state.items[id])
      delete(state.children[id])
    })
  },
  swapLinkChildren: (state, action) => {
    // const {
    //   itemid,
    //   fromid,
    //   toid,
    // } = action.payload
    // const item = state.items[itemid]
    // const links = item.resource.links
    // const fromIndex = links.findIndex(link => link.resource.id == fromid)
    // const toIndex = links.findIndex(link => link.resource.id == toid)
    // const fromLink = links[fromIndex]
    // const toLink = links[toIndex]
    // links[fromIndex] = toLink
    // links[toIndex] = fromLink
  },
  swapFolderChildren: (state, action) => {
    const {
      dragItem,
      dropItem,
    } = action.payload

    const model = QuoteModel({
      items: state.items,
      children: state.children,
      expanded: state.expanded,
    })

    model.handleDrag({
      dragItem,
      dropItem,
    })
  },

}

const loaders = {

  list: (state) => axios.get(api.projectUrl(state, '/quote'))
    .then(api.process),

  get: (state, id) => axios.get(api.projectUrl(state, `/quote/${id}`))
    .then(api.process),

  create: (state, data) => axios.post(api.projectUrl(state, `/quote`), data)
    .then(api.process),

  save: (state, id, data) => axios.put(api.projectUrl(state, `/quote/${id}`), data)
    .then(api.process),

  delete: (state, id) => axios.delete(api.projectUrl(state, `/quote/${id}`))
    .then(api.process),

}

const sideEffects = {

  list: () => networkWrapper({
    prefix,
    name: 'list',
    handler: async (dispatch, getState) => {
      const data = await loaders.list(getState())
      dispatch(actions.setQuotes(data))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error listing the quotes: ${errorMessage}`))
    }
  }),

  get: (id) => networkWrapper({
    prefix,
    name: 'get',
    handler: async (dispatch, getState) => {
      const data = await loaders.get(getState(), id)
      dispatch(actions.setQuote(data))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error getting the quote: ${errorMessage}`))
    }
  }),

  saveDetails: (id, data) => networkWrapper({
    prefix,
    name: 'saveDetails',
    handler: async (dispatch, getState) => {
      let loader = null
      if(id == 'new') {
        loader = () => loaders.create(getState(), data)
      }
      else {
        const meta = Object.assign({}, data.meta)
        const items = selectors.quote.items(getState())
        const children = selectors.quote.children(getState())
        const inputs = selectors.quote.inputs(getState())
        const calculated = selectors.quote.calculated(getState())
        meta.items = items
        meta.children = children
        meta.inputs = inputs
        meta.markups = calculated.markups
        meta.totals = calculated.totals
        loader = () => loaders.save(getState(), id, Object.assign({}, data, {meta}))
      }
      const newQuote = await loader()
      dispatch(snackbarActions.setSuccess(`quote ${ id == 'new' ? 'created' : 'saved' }`))
      if(id == 'new') {
        dispatch(routerActions.navigateTo('quote.edit.folder.tasks', {id: newQuote.id, folderid: 'root'}))
      }
      else {
        dispatch(routerActions.navigateTo('quote.list'))
      }
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error saving the quote: ${errorMessage}`))
    }
  }),

  saveQuote: () => networkWrapper({
    prefix,
    name: 'saveQuote',
    handler: async (dispatch, getState) => {
      const quote = selectors.quote.collection.item(getState())
      const meta = Object.assign({}, quote.meta)
      const items = selectors.quote.items(getState())
      const children = selectors.quote.children(getState())
      const inputs = selectors.quote.inputs(getState())
      const calculated = selectors.quote.calculated(getState())
      meta.items = items
      meta.children = children
      meta.inputs = inputs
      meta.markups = calculated.markups
      meta.totals = calculated.totals
      await loaders.save(getState(), quote.id, {
        meta,
      })
      dispatch(actions.resetChanges())
      dispatch(actions.setSortMode(false))
      dispatch(snackbarActions.setSuccess(`quote saved`))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error saving the quote: ${errorMessage}`))
    }
  }),

  delete: (id) => networkWrapper({
    prefix,
    name: 'delete',
    handler: async (dispatch, getState) => {
      await loaders.delete(getState(), id)
      dispatch(snackbarActions.setInfo(`quote deleted`))
      dispatch(actions.list())
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error deleting the quote: ${errorMessage}`))
    }
  }),

  viewFolder: (id) => networkWrapper({
    prefix,
    name: 'viewFolder',
    handler: async (dispatch, getState) => {
      const queryParams = selectors.router.params(getState())
      const route = selectors.router.route(getState())
      dispatch(actions.setSortMode(false))
      dispatch(routerActions.navigateTo(route.name, {id: queryParams.id, folderid: id}))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error viewing the folder: ${errorMessage}`))
    }
  }),

  addItem: ({
    parent,
    type,
    resource,
  }) => networkWrapper({
    prefix,
    name: 'addTask',
    handler: async (dispatch, getState) => {
      const item = {
        id: uuid(),
        parent,
        type,
        resource,
      }
      dispatch(actions.addItemData({
        parent,
        item,
      }))
      if(parent != 'root') {
        dispatch(actions.setExpanded({
          id: parent,
          value: true,
        }))
      }
      dispatch(actions.setTaskWindowParent(null))
      dispatch(actions.addChange())
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error saving the task: ${errorMessage}`))
    }
  }),

  updateItem: ({
    id,
    data,
  }) => networkWrapper({
    prefix,
    name: 'updateItem',
    handler: async (dispatch, getState) => {
      dispatch(actions.updateItemData({
        id,
        data,
      }))
      dispatch(actions.addChange())
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error saving the folder: ${errorMessage}`))
    }
  }),

  deleteItem: ({
    id,
  }) => networkWrapper({
    prefix,
    name: 'deleteItem',
    handler: async (dispatch, getState) => {
      const queryParams = selectors.router.params(getState())
      const route = selectors.router.route(getState())
      const tree = selectors.quote.tree(getState())
      const deleteItem = tree.items[id]
      dispatch(actions.deleteItemData({
        id,
      }))
      dispatch(actions.addChange())
      dispatch(actions.setDeleteItem(null))
      if(queryParams.folderid == id) {
        dispatch(routerActions.navigateTo(route.name, { id: queryParams.id, folderid: deleteItem.parent }))
      }
      dispatch(snackbarActions.setSuccess(`item deleted`))
    },
    errorHandler: (dispatch, getState, errorMessage) => {
      dispatch(snackbarActions.setError(`there was an error deleting the task: ${errorMessage}`))
    }
  }),

  exportPDF: () => networkWrapper({
    prefix,
    name: 'exportPDF',
    handler: async (dispatch, getState) => {
      const calculated = selectors.quote.calculated(getState())
      const clientView = selectors.quote.clientView(getState())
      const currency = selectors.project.currency(getState())
      const inputMarkups = selectors.quote.inputMarkupFields(getState())
      const inputVat = selectors.quote.inputVatField(getState())
      const quote = selectors.quote.collection.item(getState())
      const client = selectors.quote.quoteClient(getState())
      const project = selectors.project.activeProject(getState())
      const activeProjectId = selectors.user.activeProjectId(getState())
      const token = selectors.user.token(getState())
      PDFQuote({
        quote,
        client,
        project,
        calculated,
        clientView,
        currency,
        inputMarkups,
        inputVat,
        activeProjectId,
        token,
      })
    },
  }),
}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  sideEffects,
  prefix,
})

export {
  actions,
  reducer,
}

export default actions